import { CheckIcon, ChevronDownIcon, EditIcon } from '@chakra-ui/icons'
import { Box, Flex, HStack, IconButton, StackDivider, Text, Tooltip } from '@chakra-ui/react'
import { INITIALS_CONTAINER_WIDTH } from '@components/forms/Input/Signatures/InitialsInput'
import { useAuth, useCachedUser } from '@hb/react'
import {
  capitalizeFirstLetter,
  EncounterType,
  FieldTypes,
  getExpectedInitials,
  getFullName,
  InitialsField,
} from '@hb/shared'
import { ReactNode, useMemo, useState } from 'react'
import { useField } from 'react-final-form'
import { useFormWidth } from '../../../components/forms/FinalForm/context'
import { FormElement } from '../../../components/forms/Input'
import { useGynCareView } from '../../../components/Gyn/context'
import { PracticeProviderSelect } from '../../../components/Practices/components/PracticeProviderSelect'
import { ThemeProvider } from '../../../contexts/ThemeProvider'

type ProviderViewType = 'rendering' | 'supervising' | 'attending'
type ProviderEditType = 'supervising' | 'attending'

// §A: Procedure encounter types that require a "Performed by" provider field.
const procedureEncounterTypes: EncounterType[] = [
  EncounterType.BIRTHING_CENTER_ADMISSION,
  EncounterType.ULTRASOUND,
]

// Provider label — sage-green uppercase section-label style (spec 0.6.5), tuned for the footer.
const FooterLabel = ({ children }: { children: ReactNode }) => (
  <Text
    fontSize="11px"
    color="#4a7c59"
    fontWeight={500}
    textTransform="uppercase"
    letterSpacing="0.08em"
    lineHeight={1.4}>
    {children}
  </Text>
)

const RenderingProviderField = () => {
  const { authUser } = useAuth()

  const {
    input: { value: savedProviderId },
  } = useField<string | null>('renderingProvider', { subscription: { value: true } })
  const displayProviderId = (savedProviderId || authUser?.uid) ?? null
  const { data: provider } = useCachedUser(displayProviderId)

  const providerInitialsField = useMemo<InitialsField>(
    () => ({
      type: FieldTypes.INITIALS,
      placeholder: 'Initials',
      patientName: getFullName(provider),
      expectedInitials: provider ? getExpectedInitials(provider) : '',
    }),
    [provider],
  )

  const initialsDisabledReason = useMemo(() => {
    if (!displayProviderId) return 'No provider'
    if (authUser?.uid !== displayProviderId) return 'Can only be edited by the provider'
    return undefined
  }, [authUser?.uid, displayProviderId])

  return (
    <Box w="100%">
      <FooterLabel>Rendering Provider</FooterLabel>
      <Flex align="center" gap={2} w="100%">
        <Box flex={1} minW={0}>
          <Text fontSize="12px" color={provider ? '#333333' : '#999999'} noOfLines={1} isTruncated>
            {provider ? getFullName(provider) : savedProviderId ? 'Loading...' : 'Set on save'}
          </Text>
        </Box>
        <Box w={INITIALS_CONTAINER_WIDTH.sm}>
          <FormElement
            size="sm"
            field={providerInitialsField}
            disabled={!!initialsDisabledReason}
            disabledReason={initialsDisabledReason}
            name="renderingProviderInitials"
          />
        </Box>
      </Flex>
    </Box>
  )
}

const ProviderField = ({ type }: { type: ProviderEditType }) => {
  const { practiceId } = useGynCareView()
  const { authUser } = useAuth()

  const {
    input: { value: providerId },
  } = useField<string | null>(`${type}Provider`, { subscription: { value: true } })
  const { data: provider } = useCachedUser(providerId)

  const providerInitialsField = useMemo<InitialsField>(() => {
    const expectedInitials = provider ? getExpectedInitials(provider) : ''
    return {
      type: FieldTypes.INITIALS,
      placeholder: 'Initials',
      patientName: getFullName(provider),
      expectedInitials,
    }
  }, [provider])

  const initialsDisabledReason = useMemo(() => {
    if (!providerId) return 'No provider selected'
    if (authUser?.uid !== providerId) return 'Can only be edited by the provider'
    return undefined
  }, [authUser?.uid, providerId])

  return (
    <Box w="100%">
      <FooterLabel>{capitalizeFirstLetter(type)} Provider</FooterLabel>
      <Flex align="center" gap={2} w="100%">
        <Box flex={1} minW={0}>
          <PracticeProviderSelect
            practiceId={practiceId}
            name={`${type}Provider`}
            size="sm"
            placeholder={`${capitalizeFirstLetter(type)} Provider`}
            // style={{ padding: '0.35rem' }}
          />
        </Box>
        <Box w={INITIALS_CONTAINER_WIDTH.sm}>
          <FormElement
            size="sm"
            field={providerInitialsField}
            disabled={!!initialsDisabledReason}
            disabledReason={initialsDisabledReason}
            name={`${type}ProviderInitials`}
          />
        </Box>
      </Flex>
    </Box>
  )
}

// §A: "Performed by" field — shown only for procedure encounter types.
const PerformingProviderField = () => {
  const { practiceId } = useGynCareView()

  return (
    <Box w="100%">
      <FooterLabel>Performed by</FooterLabel>
      <PracticeProviderSelect
        practiceId={practiceId}
        name="performingProvider"
        placeholder="Performed by"
        style={{ padding: '0.35rem' }}
        inputStyle={{ fontSize: '0.875rem', lineHeight: '0.9rem' }}
      />
    </Box>
  )
}

const ProviderViewItem = ({ type, compact }: { type: ProviderViewType; compact?: boolean }) => {
  const { authUser } = useAuth()
  const {
    input: { value: savedProviderId },
  } = useField<string | null>(`${type}Provider`, { subscription: { value: true } })
  const {
    input: { value: initials },
  } = useField<string | null>(`${type}ProviderInitials`, { subscription: { value: true } })
  const displayProviderId =
    type === 'rendering' ? ((savedProviderId || authUser?.uid) ?? null) : savedProviderId
  const { data: provider } = useCachedUser(displayProviderId)

  const name = provider ? getFullName(provider) : null

  const nameText = (
    <Flex align="center" gap={1.5} flex={1} minW={0} overflow="hidden">
      <Text
        fontSize="13px"
        color={name ? '#333333' : '#999999'}
        noOfLines={1}
        flex={compact ? 'unset' : 1}
        isTruncated
        w={compact ? '100%' : 'auto'}
        minW={0}>
        {name ?? 'None'}
      </Text>
      {initials ? (
        <CheckIcon boxSize={2.5} color="green.500" flexShrink={0} />
      ) : (
        <Box boxSize={2.5} flexShrink={0} />
      )}
    </Flex>
  )

  if (compact) {
    return (
      <Tooltip label={`${capitalizeFirstLetter(type)} Provider`} placement="top" openDelay={400}>
        <Flex flex={1} flexFlow="column" w="100%" minW={0}>
          <Box w="90px" flexShrink={0}>
            <FooterLabel>{capitalizeFirstLetter(type)}</FooterLabel>
          </Box>
          {nameText}
        </Flex>
      </Tooltip>
    )
  }

  return (
    <Tooltip label={`${capitalizeFirstLetter(type)} Provider`} placement="top" openDelay={400}>
      <Flex flex={1} minW={0} flexFlow="column">
        <FooterLabel>{capitalizeFirstLetter(type)} Provider</FooterLabel>
        {nameText}
      </Flex>
    </Tooltip>
  )
}

const ProviderViewRow = () => {
  const { mobileLayout } = useFormWidth()
  if (mobileLayout) {
    return (
      <Flex flex={1} minW={0} gap={2} px={1}>
        <ProviderViewItem type="rendering" compact />
        <ProviderViewItem type="supervising" compact />
        <ProviderViewItem type="attending" compact />
      </Flex>
    )
  }
  return (
    <HStack flex={1} minW={0} spacing={3} px={1} divider={<StackDivider />}>
      <ProviderViewItem type="rendering" />
      <ProviderViewItem type="supervising" />
      <ProviderViewItem type="attending" />
    </HStack>
  )
}

const SmallEncounterFormFooterFields = ({ isProcedureType }: { isProcedureType: boolean }) => (
  <>
    <RenderingProviderField />
    <ProviderField type="supervising" />
    <ProviderField type="attending" />
    {isProcedureType && <PerformingProviderField />}
  </>
)

const LargeEncounterFormFooterFields = ({ isProcedureType }: { isProcedureType: boolean }) => (
  <Flex w="100%" flexFlow="column" gap={1}>
    <HStack w="100%" spacing={5} px={2} divider={<StackDivider />}>
      <Box flex={1} minW={0}>
        <RenderingProviderField />
      </Box>
      <Box flex={1} minW={0}>
        <ProviderField type="supervising" />
      </Box>
      <Box flex={1} minW={0}>
        <ProviderField type="attending" />
      </Box>
      {isProcedureType && (
        <Box flex={1} minW={0}>
          <PerformingProviderField />
        </Box>
      )}
    </HStack>
  </Flex>
)

const MediumExamFormFooterFields = ({ isProcedureType }: { isProcedureType: boolean }) => (
  <Flex w="100%" flexFlow="column" gap={1}>
    <HStack w="100%" spacing={3} px={2} divider={<StackDivider />}>
      <Box flex={1} minW={0}>
        <RenderingProviderField />
      </Box>
      <Box flex={1} minW={0}>
        <ProviderField type="supervising" />
      </Box>
    </HStack>
    <HStack w="100%" spacing={3} px={2} divider={<StackDivider />}>
      <Box flex={1} minW={0}>
        <ProviderField type="attending" />
      </Box>
      {isProcedureType && (
        <Box flex={1} minW={0}>
          <PerformingProviderField />
        </Box>
      )}
    </HStack>
  </Flex>
)

export const EncounterFormFooter = ({ encounterType }: { encounterType: EncounterType }) => {
  const { width } = useFormWidth()
  const [isEditing, setIsEditing] = useState(false)

  const isProcedureType = procedureEncounterTypes.includes(encounterType)

  const editBody = useMemo(() => {
    if (width < 700) return <SmallEncounterFormFooterFields isProcedureType={isProcedureType} />
    if (width < 1200) return <MediumExamFormFooterFields isProcedureType={isProcedureType} />
    return <LargeEncounterFormFooterFields isProcedureType={isProcedureType} />
  }, [width, isProcedureType])

  return (
    <ThemeProvider placeholderAbove={false} theme="detailed">
      <Flex
        flexFlow="column"
        gap={1}
        borderTop="0.5px solid #ebebeb"
        bg="#f7f8f7"
        _dark={{ bg: 'gray.800' }}
        px={width < 700 ? 2 : 5}
        pt={width < 700 ? 2 : 3}
        pb={width < 700 ? 2 : 3}
        w="100%"
        align="center">
        <Flex w="100%" gap={2}>
          <Box flex={1} minW={0}>
            {isEditing ? editBody : <ProviderViewRow />}
          </Box>
          <Tooltip label={isEditing ? 'Collapse providers' : 'Edit providers'} placement="top">
            <IconButton
              aria-label={isEditing ? 'Collapse providers' : 'Edit providers'}
              icon={isEditing ? <ChevronDownIcon w={4} h={4} /> : <EditIcon />}
              size="sm"
              variant="ghost"
              colorScheme="gray"
              onClick={() => setIsEditing(v => !v)}
            />
          </Tooltip>
        </Flex>
      </Flex>
    </ThemeProvider>
  )
}
